// Persistent top header — rendered once by AppLayout, so it stays fixed on
// every authenticated page. Shows: avatar + user name, mobile number, a location
// switcher (all permitted locations), a menu to the secondary screens, Logout.
// Reads everything from AppContext so it works regardless of the current route.

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Avatar, Box, Typography, Button, MenuItem, Select,
  FormControl, IconButton, Menu, ListItemIcon, ListItemText, Tooltip, Chip,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import StarIcon from '@mui/icons-material/Star';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InfoIcon from '@mui/icons-material/Info';
import BusinessIcon from '@mui/icons-material/Business';
import MemoryIcon from '@mui/icons-material/Memory';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useApp } from '../state/AppContext';
import { arrayMove } from '../utils/reorder';

export default function DesktopHeader() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    user, locations, locationId, setLocationId, logout, showCommon, setShowCommon,
    isSuperAdmin, companyName, canReorder, editOrder, setEditOrder, reorderLocations,
  } = useApp();
  const [anchor, setAnchor] = useState(null);
  const [locMenu, setLocMenu] = useState(null);
  const [dragI, setDragI] = useState(null);
  const onHome = pathname === '/home';
  const onFav = pathname === '/favourites';
  const activeSx = (active) => ({ bgcolor: active ? 'rgba(255,255,255,0.18)' : 'transparent' });

  const fullName = user
    ? [user.Surname, user.Name, user.FatherHusband].filter(Boolean).join(' ')
    : 'User';
  const initials = (fullName || 'U').trim().charAt(0).toUpperCase();

  const go = (path) => { setAnchor(null); navigate(path); };
  const onLogout = () => { logout(); navigate('/login', { replace: true }); };

  return (
    <AppBar position="static" elevation={2}>
      <Toolbar sx={{ gap: 2 }}>
        <IconButton color="inherit" edge="start" onClick={(e) => setAnchor(e.currentTarget)}>
          <MenuIcon />
        </IconButton>
        <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
          <MenuItem onClick={() => go('/home')}>
            <ListItemIcon><HomeIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Home</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => go('/favourites')}>
            <ListItemIcon><StarIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Favourites</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => go('/scenes')}>
            <ListItemIcon><GroupWorkIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Scenes</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => go('/wall')}>
            <ListItemIcon><DashboardIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Wall Mode</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => go('/about')}>
            <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
            <ListItemText>About</ListItemText>
          </MenuItem>
          {isSuperAdmin && (
            <MenuItem onClick={() => go('/companies')}>
              <ListItemIcon><BusinessIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Companies (Admin)</ListItemText>
            </MenuItem>
          )}
          {isSuperAdmin && (
            <MenuItem onClick={() => go('/device-types')}>
              <ListItemIcon><MemoryIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Device Types (Admin)</ListItemText>
            </MenuItem>
          )}
        </Menu>

        {/* Quick nav */}
        <Tooltip title="Home">
          <IconButton color="inherit" onClick={() => navigate('/home')} sx={activeSx(onHome)}>
            <HomeIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Favourites">
          <IconButton color="inherit" onClick={() => navigate('/favourites')} sx={activeSx(onFav)}>
            <StarIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Profile">
          <Box
            onClick={() => navigate('/profile')}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', minWidth: 0 }}
          >
            <Avatar sx={{ bgcolor: 'primary.dark', color: 'primary.contrastText' }}>
              {initials || <PersonIcon />}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography fontWeight={700} noWrap>{fullName}</Typography>
                {isSuperAdmin && (
                  <Chip
                    label="Super Admin" size="small" color="secondary"
                    sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700 }}
                  />
                )}
              </Box>
              <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap>
                {user?.MobileNumber}{user?.UserId != null ? ` · ID ${user.UserId}` : ''}
              </Typography>
            </Box>
          </Box>
        </Tooltip>

        {companyName && (
          <Tooltip title={isSuperAdmin ? 'Manage Company Info' : ''}>
            <Box
              onClick={isSuperAdmin ? () => navigate('/companies') : undefined}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.75, ml: 1, px: 1.25, py: 0.5,
                borderRadius: 2, bgcolor: 'rgba(255,255,255,0.12)',
                cursor: isSuperAdmin ? 'pointer' : 'default',
              }}
            >
              <BusinessIcon fontSize="small" />
              <Typography fontWeight={700} noWrap>{companyName}</Typography>
              {isSuperAdmin && <EditIcon sx={{ fontSize: 15, opacity: 0.85 }} />}
            </Box>
          </Tooltip>
        )}

        <Box sx={{ flex: 1 }} />

        {/* Edit-order toggle (Super Admin or location owner). */}
        {canReorder && (
          <Tooltip title={editOrder ? 'Finish reordering' : 'Edit order of rooms, devices & locations'}>
            <Button
              variant={editOrder ? 'contained' : 'outlined'}
              color={editOrder ? 'secondary' : 'inherit'}
              startIcon={<SwapVertIcon />}
              onClick={() => setEditOrder((v) => !v)}
              sx={{ whiteSpace: 'nowrap', borderColor: 'rgba(255,255,255,0.5)' }}
            >
              {editOrder ? 'Done' : 'Edit order'}
            </Button>
          </Tooltip>
        )}

        {/* Reorder locations (only while editing order). */}
        {canReorder && editOrder && (
          <>
            <Tooltip title="Reorder locations">
              <IconButton color="inherit" onClick={(e) => setLocMenu(e.currentTarget)}>
                <DragIndicatorIcon />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={locMenu} open={!!locMenu} onClose={() => setLocMenu(null)}>
              <MenuItem disabled sx={{ opacity: 0.8 }}>
                <ListItemText primaryTypographyProps={{ variant: 'caption' }}>
                  Drag to reorder locations
                </ListItemText>
              </MenuItem>
              {locations.map((l, i) => (
                <MenuItem
                  key={l.LocationID}
                  draggable
                  onDragStart={(e) => { setDragI(i); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); }}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragI != null && dragI !== i) reorderLocations(arrayMove(locations, dragI, i));
                    setDragI(null);
                  }}
                  sx={{ cursor: 'grab' }}
                >
                  <ListItemIcon><DragIndicatorIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{l.LocationName || `Location ${l.LocationID}`}</ListItemText>
                  <Box sx={{ display: 'flex' }} onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" disabled={i === 0} onClick={() => reorderLocations(arrayMove(locations, i, i - 1))}><KeyboardArrowUpIcon fontSize="small" /></IconButton>
                    <IconButton size="small" disabled={i === locations.length - 1} onClick={() => reorderLocations(arrayMove(locations, i, i + 1))}><KeyboardArrowDownIcon fontSize="small" /></IconButton>
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </>
        )}

        {/* Common Devices toggle (Home only) — green = shown, red = hidden. */}
        {onHome && (
          <Tooltip title={showCommon ? 'Hide common devices' : 'Show common devices'}>
            <Button
              variant="contained"
              color={showCommon ? 'success' : 'error'}
              startIcon={<GroupWorkIcon />}
              onClick={() => setShowCommon((v) => !v)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Common Devices
            </Button>
          </Tooltip>
        )}

        {/* Location switcher — current location is always visible here. */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select
            value={locations.length ? (locationId ?? '') : ''}
            displayEmpty
            onChange={(e) => setLocationId(e.target.value)}
            sx={{
              color: 'inherit',
              '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
              '.MuiSvgIcon-root': { color: 'inherit' },
            }}
            renderValue={(val) => {
              const loc = locations.find((l) => String(l.LocationID) === String(val));
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOnIcon fontSize="small" />
                  <Typography noWrap>
                    {loc ? (loc.LocationName || `Location ${loc.LocationID}`) : 'Select location'}
                  </Typography>
                </Box>
              );
            }}
          >
            {locations.map((loc) => (
              <MenuItem key={loc.LocationID} value={loc.LocationID}>
                {loc.LocationName || `Location ${loc.LocationID}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip title="Logout">
          <Button color="inherit" startIcon={<LogoutIcon />} onClick={onLogout}>
            Logout
          </Button>
        </Tooltip>
      </Toolbar>
    </AppBar>
  );
}
